/**
 * Branch menu strip pass: workspace + profile (config-driven) only.
 * Sidebar no longer runs this path; registry from the API is PCS-filtered. Platform/org skip this module.
 */

import { logger } from '@/lib/logger'

export type ProfileBundle = {
  primary: string[]
  functional: string[]
}

/**
 * One row per path key in the branch menu (see `menu-visibility.map.ts`).
 * `isPersonal` tags items under the personal “My Workspace” area (for generators / validation).
 * `allowChildrenOverride` optional — set in the map for `my-workspace` (grouping rule); strip pass keeps parents only when they have visible children.
 */
export type MenuVisibilityConfig = {
  allowedWorkspaces: string[]
  /** If non-empty, user must have at least one of these in primary ∪ functional. */
  requiredProfiles?: string[]
  isPersonal: boolean
  allowChildrenOverride?: boolean
  /**
   * When true, this path’s config does not apply to longer paths (no hierarchical resolution
   * to this node as a parent fallback).
   */
  strict?: boolean
}

/**
 * Fixed catalog — must match backend `WORKSPACE_ORDER` / `user-profiles.service.ts`.
 */
export const WORKSPACE_ORDER: readonly string[] = [
  'admin',
  'teacher',
  'student',
  'parent',
  'admissions',
  'finance',
  'hostel',
  'transport',
]

export const ALL_BRANCH_WORKSPACE_IDS: readonly string[] = [...WORKSPACE_ORDER]

/**
 * Sort available workspaces deterministically.
 */
export function sortWorkspacesByOrder(available: string[]): string[] {
  const set = new Set(available)
  const ordered: string[] = []
  for (const w of WORKSPACE_ORDER) {
    if (set.has(w)) ordered.push(w)
  }
  for (const a of available) {
    if (!WORKSPACE_ORDER.includes(a)) ordered.push(a)
  }
  return ordered
}

/**
 * True only when the active value is a non-empty list and is contained in the ordered set.
 * Does not pick a default — use `coalesceWorkspaceWithProfiles` for that (with logging).
 */
export function validateWorkspace(
  activeWorkspace: string | null | undefined,
  availableWorkspaces: string[]
): string | null {
  const ordered = sortWorkspacesByOrder(availableWorkspaces)
  if (ordered.length === 0) return null
  if (activeWorkspace == null || !ordered.includes(activeWorkspace)) {
    return null
  }
  return activeWorkspace
}

const workspaceProfileFallbackLogged = new Set<string>()

/** Hard cap for API / bootstrap `workspaces` array length; prevents unbounded client state. */
export const MAX_USER_WORKSPACES = 10

/**
 * @deprecated Server bootstrap `workspaces` is authoritative (PCS + RoleCapability).
 * Kept for legacy callers — mirrors functional tags only, never elevates admin from identity.
 */
export function getAvailableWorkspacesForProfiles(profiles: ProfileBundle): string[] {
  const p = new Set(profiles.primary)
  const f = new Set(profiles.functional)
  const raw: string[] = []
  if (f.has('teacher') || f.has('academic_lead')) raw.push('teacher')
  if (p.has('student')) raw.push('student')
  if (p.has('parent')) raw.push('parent')
  if (f.has('admissions') || f.has('reception')) raw.push('admissions')
  if (f.has('finance')) raw.push('finance')
  if (f.has('hostel')) raw.push('hostel')
  if (f.has('transport')) raw.push('transport')
  return sortWorkspacesByOrder(raw)
}

export function isWorkspaceAllowedForProfiles(
  workspace: string,
  _profiles: ProfileBundle,
  availableWorkspaces?: string[]
): boolean {
  if (Array.isArray(availableWorkspaces) && availableWorkspaces.length > 0) {
    return availableWorkspaces.includes(workspace)
  }
  return getAvailableWorkspacesForProfiles(_profiles).includes(workspace)
}

/**
 * Picks a shell from server-computed `availableWorkspaces` (PCS + capabilities).
 * Profiles are ignored for gating — bootstrap is authoritative.
 */
export function coalesceWorkspaceWithProfiles(
  activeWorkspace: string | null,
  availableWorkspaces: string[],
  _profiles: ProfileBundle
): string | null {
  let list = availableWorkspaces
  if (list.length > MAX_USER_WORKSPACES) {
    logger.error(
      '[menu-visibility] Workspace list exceeds maximum; using first N after canonical sort.',
      { max: MAX_USER_WORKSPACES, count: list.length, availableWorkspaces: list }
    )
    list = sortWorkspacesByOrder(list).slice(0, MAX_USER_WORKSPACES)
  }

  const ordered = sortWorkspacesByOrder(list)
  if (ordered.length === 0) {
    logger.error('[menu-visibility] No available workspaces; cannot select shell.', { activeWorkspace })
    return null
  }

  const validated = validateWorkspace(activeWorkspace, ordered)
  if (validated) return validated

  const fallback = ordered[0] ?? null
  if (fallback && !workspaceProfileFallbackLogged.has('active-invalid->first-server')) {
    workspaceProfileFallbackLogged.add('active-invalid->first-server')
    logger.error('[menu-visibility] activeWorkspace invalid; using first server workspace.', {
      from: activeWorkspace,
      to: fallback,
      availableWorkspaces: ordered,
    })
  }
  return fallback
}
